<!--
  "Can I come in?"

  A friend asked to join the room you are in. The server has NOT put them in it and will
  not: it carries the question to every live socket you have, carries your answer back,
  and their client does the joining with the room code it is given. A room's membership
  only ever changes because a client sent `join`, and this prompt is the whole reason
  that rule survives contact with a social feature.

  WHY IT IS FIXED TO THE VIEWPORT. The question is time-boxed — sixty seconds, decided by
  the server — and it can arrive while you are anywhere. A card inside a scrolling page
  would be a question you never saw. It sits above the phone tab bar and clears the home
  indicator; it is not drawn over the film, because the watch screen mounts none of this
  (see the report: one line in App.svelte would fix that, and that file is not mine).

  UNANSWERED IS NOT DECLINED. Letting it run out sends nothing at all — the server
  expires it on its own tick and tells the asker "did not answer", which is a different
  and kinder sentence than "said no". The countdown is drawn so nobody has to guess.
-->
<script lang="ts">
  import Button from '$lib/ui/Button.svelte';
  import AvatarMark from './AvatarMark.svelte';
  import { personName } from './people';
  import type { IncomingAsk } from './store';

  interface Props {
    asks: IncomingAsk[];
    onanswer: (requestId: string, accept: boolean) => void;
    /** Called about once a second so the store can drop what has run out. */
    ontick?: () => void;
  }

  let { asks, onanswer, ontick }: Props = $props();

  let now = $state(Date.now());

  $effect(() => {
    if (asks.length === 0) return;
    // Re-read the clock BEFORE the first tick. Without this the first frame of a new
    // prompt is drawn against whatever `now` was when the last prompt was answered —
    // measured at 8s remaining on a request with a 6s life, which is a countdown lying
    // about the one number it exists to show.
    now = Date.now();
    const id = setInterval(() => {
      now = Date.now();
      ontick?.();
    }, 1000);
    return () => clearInterval(id);
  });

  /** Oldest first: the person who has been waiting longest is answered first. */
  const queue = $derived([...asks].sort((a, b) => a.expiresAt - b.expiresAt));

  function left(ask: IncomingAsk): number {
    return Math.max(0, Math.ceil((ask.expiresAt - now) / 1000));
  }
</script>

{#if queue.length > 0}
  <div class="dock" role="region" aria-label="Requests to join you">
    {#each queue as ask (ask.requestId)}
      <div class="card">
        <AvatarMark avatar={ask.from.avatar} color={ask.from.color} size={40} square />
        <div class="what">
          <p class="line">
            <b>{personName(ask.from)}</b> wants to watch with you
          </p>
          <p class="sub">
            <span class="handle">@{ask.from.handle}</span>
            <span aria-hidden="true">·</span>
            <span class="left">{left(ask)}s to answer</span>
          </p>
        </div>
        <div class="acts">
          <Button onclick={() => onanswer(ask.requestId, false)}>No</Button>
          <Button variant="primary" onclick={() => onanswer(ask.requestId, true)}>Let them in</Button>
        </div>
      </div>
    {/each}
  </div>
{/if}

<style>
  .dock {
    position: fixed;
    left: max(var(--space-2), var(--safe-l));
    right: max(var(--space-2), var(--safe-r));
    /* Above the phone tab bar (56px + inset) with room to breathe, and above the
       desktop's nothing. One number either way; the tab bar owns its own height. */
    bottom: calc(var(--space-2) + var(--safe-b) + 60px);
    z-index: 60;
    display: grid;
    gap: var(--space-2);
    justify-items: center;
    pointer-events: none;
  }

  .card {
    pointer-events: auto;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--space-3);
    width: min(100%, 460px);
    padding: var(--space-3);
    border-radius: var(--radius-lg);
    border: 1px solid color-mix(in oklab, var(--accent) 40%, var(--line));
    background: color-mix(in oklab, var(--accent) 10%, var(--surface-1));
    box-shadow: 0 18px 40px rgb(0 0 0 / 0.5);
  }

  .what {
    flex: 1 1 150px;
    min-width: 0;
  }
  .line {
    font-size: var(--text-sm);
    color: var(--text-dim);
  }
  .line b {
    color: var(--text);
    font-weight: var(--weight-semi);
  }
  .sub {
    display: flex;
    gap: 6px;
    margin-top: 1px;
    color: var(--text-faint);
    font-size: var(--text-xs);
  }
  .handle {
    font-family: var(--mono);
  }
  .left {
    font-variant-numeric: tabular-nums;
  }

  .acts {
    display: flex;
    gap: var(--space-2);
    margin-left: auto;
  }

  @media (min-width: 768px) {
    .dock {
      bottom: calc(var(--space-4) + var(--safe-b));
    }
  }
</style>
